import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Drawer } from "antd";
import Topbar from "@/components/layout/Topbar";
import Sidebar from "@/components/layout/Sidebar";
import FullScreenLoader from "@/components/ui/FullScreenLoader";
import Icon from "@/components/ui/Icon";
import { useSubscription } from "@/hooks/useSubscription";
import { useUserRoles } from "@/hooks/useUserRoles";
import { NAV_MODULES, canSee, findActiveModule } from "@/routes/navigation";
import styles from "./styles.module.scss";

// Preferência visual do menu lateral no desktop (não é dado de sessão).
const SIDEBAR_KEY = "yuha_sidebar_collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  } catch {
    return false;
  }
};

// Esqueleto das páginas autenticadas: topbar fixa, menu lateral do módulo
// atual e área de conteúdo com rolagem própria. Início não tem menu lateral.
const DashboardLayout: React.FC = () => {
  const { pathname } = useLocation();
  const { roles } = useUserRoles();
  const { subscription, loading, error } = useSubscription();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const module = findActiveModule(pathname);
  const showSidebar = !!module && module.key !== "inicio" && module.pages.length > 0;

  useEffect(() => {
    document.getElementById("dash-content")?.scrollTo(0, 0);
  }, [pathname]);

  const toggleSidebar = () => {
    setCollapsed((current) => {
      try {
        localStorage.setItem(SIDEBAR_KEY, current ? "0" : "1");
      } catch {
        // Sem storage: a preferência vale só até recarregar.
      }
      return !current;
    });
  };

  // Mocks da conta Preview e os bloqueios por plano dependem da assinatura
  // já estar no cache antes de as páginas buscarem dados.
  if (!subscription && loading && !error) return <FullScreenLoader />;

  return (
    <div className={styles.shell}>
      <Topbar activeModule={module?.key} onOpenMobileNav={() => setMobileNavOpen(true)} />
      <div className={styles.body}>
        {showSidebar && module && (
          <aside className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ""}`}>
            <div className={styles.sidebarScroll}>
              <Sidebar module={module} collapsed={collapsed} />
            </div>
            <button
              type="button"
              className={styles.sidebarToggle}
              onClick={toggleSidebar}
              aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
              aria-expanded={!collapsed}
              title={collapsed ? "Expandir menu" : "Recolher menu"}
            >
              <Icon name={collapsed ? "chevronRight" : "chevronLeft"} size={16} />
            </button>
          </aside>
        )}
        <main id="dash-content" className={styles.content}>
          <div className={`${styles.page} ${showSidebar ? "" : styles.pageWide}`}>
            <Outlet />
          </div>
        </main>
      </div>

      <Drawer
        placement="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        width={300}
        title="Menu"
      >
        <div className={styles.mobileNav}>
          {NAV_MODULES.filter((m) => !m.comingSoon && canSee(m.roles, roles)).map((m) =>
            m.key === "inicio" ? (
              <Link key={m.key} to={m.pages[0].path} className={styles.mobileHome} onClick={() => setMobileNavOpen(false)}>
                Início
              </Link>
            ) : (
              <Sidebar key={m.key} module={m} onNavigate={() => setMobileNavOpen(false)} />
            ),
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default DashboardLayout;
